import { useAuthorisation } from '@/composables/authorisation'
import { ImageType } from '@/enums'
import { useAuthStore } from '@/pinia/auth'
import { useBasketStore } from '@/pinia/basket'
import { useStyleStore } from '@/pinia/style'
import { useVisualiserStore } from '@/pinia/visualiser'
import alertbox from '@/utils/alertbox'

const routes = [
  {
    path: '/portal',
    redirect: '/'
  },
  {
    path: '/',
    name: 'Portal Dashboard',
    component: () => import('@/views/Portal/Dashboard.vue'),
    meta: { active: 'home' }
  },
  {
    path: '/setup',
    component: () => import('@/views/Portal/Setup.vue'),
    redirect: '/',
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore()
      if (authStore.admin) {
        next()
      } else {
        await alertbox({
          title: 'Redirected Home',
          text: 'Admin users only'
        })
        next('/')
      }
    },
    children: [
      {
        path: 'account',
        name: 'Account',
        meta: { active: 'account', root: 'setup' },
        component: () => import('@/views/Portal/Setup/Account.vue'),
        beforeEnter: (to, from, next) => {
          const { hasFullAccess } = useAuthorisation()
          if (hasFullAccess.value) {
            next()
          } else {
            next('/')
          }
        }
      },
      {
        path: 'staff',
        name: 'Staff',
        meta: { active: 'staff', root: 'setup', showSearch: true },
        component: () => import('@/views/Portal/Setup/Staff.vue'),
        beforeEnter: (to, from, next) => {
          const { hasFullAccess } = useAuthorisation()
          if (hasFullAccess.value) {
            next()
          } else {
            next('/')
          }
        }
      },
      {
        path: 'styling',
        name: 'Styling',
        meta: { active: 'styling', root: 'setup' },
        component: () => import('@/views/Portal/Setup/Styling.vue'),
        beforeEnter: (to, from, next) => {
          const { hasFullAccess } = useAuthorisation()
          if (hasFullAccess.value) {
            next()
          } else {
            next('/')
          }
        }
      },
      {
        path: 'costs-prices',
        name: 'Costs Prices',
        meta: { active: 'costs-prices', root: 'setup' },
        component: () => import('@/views/Portal/Setup/CostsPrices.vue'),
        redirect: '/setup/costs-prices/pricing-setup',
        beforeEnter: (to, from, next) => {
          const { hasFullAccess } = useAuthorisation()
          const authStore = useAuthStore()
          if (hasFullAccess.value && authStore.isExtruderOrExpress) {
            next()
          } else {
            next('/')
          }
        },
        children: [
          {
            path: 'pricing-setup',
            name: 'Pricing Setup',
            meta: { active: 'costs-prices', tab: 'pricing-setup', root: 'setup' },
            component: () => import('@/views/Portal/Setup/CostsPrices/PricingSetup.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.isExtruderOrExpress) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'financial-categories',
            name: 'Financial Categories',
            meta: { active: 'costs-prices', tab: 'financial-categories', root: 'setup' },
            component: () => import('@/views/Portal/Setup/CostsPrices/FinancialCategories.vue')
          },
          {
            path: 'parts-list',
            name: 'Parts List',
            meta: { active: 'costs-prices', tab: 'parts-list', root: 'setup', showSearch: true },
            component: () => import('@/views/Portal/Setup/CostsPrices/PartsList.vue'),
            beforeEnter: (to, from, next) => {
              const { hasFullAccess } = useAuthorisation()
              if (hasFullAccess.value) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'glass',
            name: 'Glass',
            meta: { active: 'costs-prices', tab: 'glass', root: 'setup' },
            component: () => import('@/views/Portal/Setup/CostsPrices/Glass.vue')
          },
          {
            path: 'panel',
            name: 'Panel',
            meta: { active: 'costs-prices', tab: 'panel', root: 'setup' },
            component: () => import('@/views/Portal/Setup/CostsPrices/Panel.vue')
          },
          {
            path: 'slab-glass-bands',
            name: 'Slab Glass Bands',
            meta: { active: 'costs-prices', tab: 'slab-glass-bands', root: 'setup' },
            component: () => import('@/views/Portal/Setup/CostsPrices/SlabGlassBands.vue')
          },
          {
            path: 'slab',
            name: 'Slab',
            meta: { active: 'costs-prices', tab: 'slab', root: 'setup' },
            component: () => import('@/views/Portal/Setup/CostsPrices/Slab.vue')
          },
          {
            path: 'labour',
            name: 'Labour',
            meta: { active: 'costs-prices', tab: 'labour', root: 'setup' },
            component: () => import('@/views/Portal/Setup/CostsPrices/Labour.vue'),
            beforeEnter: (to, from, next) => {
              const { hasFullAccess } = useAuthorisation()
              if (hasFullAccess.value) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'product',
            name: 'Product',
            meta: { active: 'costs-prices', tab: 'product', root: 'setup' },
            component: () => import('@/views/Portal/Setup/CostsPrices/Product.vue')
          },
          {
            path: 'non-stock-extras',
            name: 'Non Stock Extras',
            meta: { active: 'costs-prices', tab: 'non-stock-extras', root: 'setup' },
            component: () => import('@/views/Business/Setup/CostsPrices/NonStockExtras.vue')
          }
        ]
      },
      {
        path: 'product-settings',
        name: 'Product Settings',
        meta: { active: 'product-settings', root: 'setup' },
        component: () => import('@/views/Portal/Setup/ProductSettings.vue'),
        redirect: '/setup/product-settings/general',
        beforeEnter: (to, from, next) => {
          const { hasFullAccess } = useAuthorisation()
          if (hasFullAccess.value) {
            next()
          } else {
            next('/')
          }
        },
        children: [
          {
            path: 'general',
            name: 'General',
            meta: { active: 'product-settings', tab: 'general', root: 'setup' },
            component: () => import('@/views/Portal/Setup/ProductSettings/General.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.isExtruderOrExpress) {
                next()
              } else {
                next('/setup/product-settings/requote-markups')
              }
            }
          },
          {
            path: 'requote-markups',
            name: 'Requote Markups',
            meta: { active: 'product-settings', tab: 'requote-markups', root: 'setup' },
            component: () => import('@/views/Portal/Setup/ProductSettings/RequoteMarkups.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.isExtruderOrExpress) {
                next('/setup/product-settings/alternate-glass-clearance')
              } else if (authStore.superuser) {
                next()
              } else {
                next('/')
              }
            }
          },
          {
            path: 'alternate-glass-clearance',
            name: 'Alternate Glass Clearance',
            meta: { active: 'product-settings', tab: 'alternate-glass-clearance', root: 'setup' },
            component: () => import('@/views/Portal/Setup/ProductSettings/AlternateGlassClearance.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.isExtruderOrExpress) {
                next()
              } else {
                next('/setup/product-settings/requote-markups')
              }
            }
          },
          {
            path: 'alternate-sash-overlaps',
            name: 'Alternate Sash Overlaps',
            meta: { active: 'product-settings', tab: 'alternate-sash-overlaps', root: 'setup' },
            component: () => import('@/views/Portal/Setup/ProductSettings/AlternateSashOverlaps.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.isExtruderOrExpress) {
                next()
              } else {
                next('/setup/product-settings/requote-markups')
              }
            }
          },
          {
            path: 'specifications',
            name: 'Specifications',
            meta: { active: 'product-settings', tab: 'specifications', root: 'setup' },
            component: () => import('@/views/Portal/Setup/ProductSettings/Specification.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.isExtruderOrExpress) {
                next()
              } else {
                next('/setup/product-settings/requote-markups')
              }
            }
          },
          {
            path: 'defaults',
            name: 'Defaults',
            meta: { active: 'product-settings', tab: 'defaults', root: 'setup' },
            component: () => import('@/views/Portal/Setup/ProductSettings/ProductDefaults.vue'),
            beforeEnter: (to, from, next) => {
              const authStore = useAuthStore()
              if (authStore.isExtruderOrExpress) {
                next()
              } else {
                next('/setup/product-settings/requote-markups')
              }
            }
          }
        ]
      },
      {
        path: ':mode(lead-capture)',
        name: 'Lead Capture',
        meta: { active: 'lead-capture', root: 'setup' },
        component: () => import('@/views/Portal/Setup/Retail.vue'),
        beforeEnter: async (to, from, next) => {
          const { isLeadCapturePortal } = useAuthorisation()
          if (isLeadCapturePortal.value) {
            next()
          } else {
            await alertbox({
              title: 'Redirected Home',
              text: 'No license found'
            })
            next('/')
          }
        },
        children: [
          {
            path: '',
            name: 'Lead Capture Dashboard',
            meta: { tab: 'dashboard', root: 'setup' },
            component: () => import('@/views/Portal/Setup/Retail/Dashboard.vue')
          },
          {
            path: 'company',
            name: 'Lead Capture Company Settings',
            meta: { tab: 'company', root: 'setup' },
            component: () => import('@/views/Portal/Setup/Retail/Company.vue')
          },
          {
            path: 'products',
            name: 'Lead Capture Products',
            meta: { tab: 'products', root: 'setup' },
            component: () => import('@/views/Portal/Setup/Retail/ProductAvailability.vue')
          },
          {
            path: 'styling',
            name: 'Lead Capture Styling',
            meta: { tab: 'styling', root: 'setup' },
            component: () => import('@/views/Portal/Setup/Retail/Styling.vue')
          },
          {
            path: 'deployment',
            name: 'Lead Capture Deployment',
            meta: { tab: 'deployment', root: 'setup' },
            component: () => import('@/views/Portal/Setup/Retail/Deployment.vue')
          }
        ]
      }
    ]
  },
  {
    path: '/load-contract/:contractId/:jobId',
    name: 'Load A Contract',
    component: () => import('@/views/Portal/LoadContract.vue'),
    meta: {}
  },
  {
    path: '/quotes',
    name: 'Quotes',
    meta: { showSearch: true, active: 'quotes' },
    component: () => import('@/views/Portal/Quotes.vue'),
    beforeEnter: (to, from, next) => {
      const { hasFullAccess } = useAuthorisation()
      if (!hasFullAccess.value) {
        next({ name: 'Portal Dashboard' })
      } else {
        next()
      }
    }
  },
  {
    path: '/quote/:id/:jobId?',
    name: 'Quote',
    meta: { active: 'quotes' },
    component: () => import('@/views/Portal/Quote.vue')
  },
  {
    path: '/network-enquiry',
    redirect: '/enquiries'
  },
  {
    path: '/setup/branded',
    redirect: '/setup/lead-capture'
  },
  {
    path: '/enquiries',
    name: 'Enquiries',
    meta: { showSearch: true, active: 'enquiries' },
    component: () => import('@/views/Portal/Enquiries.vue'),
    beforeEnter: (to, from, next) => {
      const { hasFullAccessLeadGenOnly, isLeadGenOnly } = useAuthorisation()
      if (hasFullAccessLeadGenOnly.value || !isLeadGenOnly.value) {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    path: '/enquiry/:id/:jobId?',
    name: 'Enquiry',
    meta: { active: 'enquiries' },
    component: () => import('@/views/Portal/Enquiry.vue'),
    beforeEnter: (to, from, next) => {
      const { hasFullAccessLeadGenOnly, isLeadGenOnly } = useAuthorisation()
      if (hasFullAccessLeadGenOnly.value || !isLeadGenOnly.value) {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    path: '/basket',
    name: 'Basket',
    meta: { active: 'designer' },
    component: () => import('@/views/Portal/Basket.vue'),
    beforeEnter: (to, from, next) => {
      const basketStore = useBasketStore()
      if (basketStore.hasContract) {
        next()
      } else {
        next('/new-quote')
      }
    }
  },
  {
    path: '/checkout',
    name: 'Checkout',
    meta: { active: 'designer' },
    component: () => import('@/views/Portal/Checkout.vue'),
    beforeEnter: (to, from, next) => {
      const basketStore = useBasketStore()
      if (basketStore.hasContract) {
        next()
      } else {
        next('/new-quote')
      }
    }
  },
  {
    path: '/choose-product',
    name: 'Choose Product',
    meta: { active: 'designer' },
    component: () => import('@/views/Portal/ChooseProduct.vue'),
    beforeEnter: (to, from, next) => {
      const basketStore = useBasketStore()
      if (basketStore.hasContract) {
        next()
      } else {
        next('/new-quote')
      }
    }
  },
  {
    path: '/choose-extras',
    name: 'Choose Extras',
    meta: { active: 'designer' },
    component: () => import('@/views/Portal/ChooseExtras.vue'),
    beforeEnter: (to, from, next) => {
      const basketStore = useBasketStore()
      if (basketStore.hasContract) {
        next()
      } else {
        next('/new-quote')
      }
    }
  },
  {
    path: '/new-quote',
    name: 'New Quote',
    meta: { active: 'designer' },
    component: () => import('@/views/Shared/NewQuote.vue'),
    beforeEnter: async (to, from, next) => {
      const { hasFullAccess } = useAuthorisation()
      const styleStore = useStyleStore()
      await styleStore.loadFabricatorStyle()
      if (styleStore.fabricator.portalOnStop) {
        await alertbox({
          title: 'System under temporary maintenance',
          text: 'Please check back soon'
        })
        next('/')
      } else if (!hasFullAccess.value) {
        next('/')
      } else {
        next()
      }
    }
  },
  {
    path: '/new-order',
    name: 'New Order',
    meta: { active: 'designer' },
    component: () => import('@/views/Shared/NewOrder.vue'),
    beforeEnter: async (to, from, next) => {
      const { hasFullAccess } = useAuthorisation()
      const styleStore = useStyleStore()
      await styleStore.loadFabricatorStyle()
      if (styleStore.fabricator.portalOnStop) {
        await alertbox({
          title: 'System under temporary maintenance',
          text: 'Please check back soon'
        })
        next('/')
      } else if (!hasFullAccess.value) {
        next('/')
      } else {
        next()
      }
    }
  },
  {
    path: '/design/:id',
    name: 'Designer',
    meta: { active: 'designer' },
    component: () => import('@/views/Portal/Designer.vue'),
    props: true,
    beforeEnter: (to, from, next) => {
      const basketStore = useBasketStore()
      if (basketStore.hasContract) {
        if (!basketStore.getContractIdentifier?.basketQty) {
          next('/choose-product')
        } else if (from.name === 'Switch Bay' && typeof to.query.usePreviousView === 'undefined') {
          const newTo = to
          newTo.query.usePreviousView = ImageType.PLAN.id
          next(newTo)
        } else {
          next()
        }
      } else {
        next('/new-quote')
      }
    }
  },
  {
    path: '/custom-designer',
    name: 'Custom Designer',
    meta: { active: 'designer' },
    component: () => import('@/views/Portal/CustomDesigner.vue'),
    props: true,
    beforeEnter: (to, from, next) => {
      const basketStore = useBasketStore()
      if (basketStore.hasContract) {
        next()
      } else {
        next('/new-quote')
      }
    }
  },
  {
    path: '/switch-design/:id/:component',
    name: 'Switch Bay',
    meta: { active: 'designer' },
    component: () => import('@/views/Portal/SwitchDesign.vue'),
    beforeEnter: (to, from, next) => {
      const basketStore = useBasketStore()
      if (basketStore.hasContract) {
        next()
      } else {
        next('/new-quote')
      }
    }
  },
  {
    path: '/orders',
    name: 'Orders',
    meta: { showSearch: true, active: 'orders' },
    component: () => import('@/views/Portal/Orders.vue'),
    beforeEnter: (to, from, next) => {
      const { hasFullAccess } = useAuthorisation()
      if (!hasFullAccess.value) {
        next('/')
      } else {
        next()
      }
    }
  },
  {
    path: '/order/:id/:jobId?',
    name: 'Order',
    meta: { active: 'orders' },
    component: () => import('@/views/Portal/Order.vue')
  },
  {
    path: '/touch-store',
    name: 'QUOTILA Store',
    meta: { active: 'touch-store' },
    component: () => import('@/views/Portal/TouchStore.vue')
  },
  {
    path: '/touch-store/more-benefits',
    name: 'More Benefits',
    meta: { active: 'more-benefits' },
    component: () => import('@/views/Portal/TouchStore/MoreBenefits.vue')
  },
  {
    path: '/marketing',
    name: 'Downloads',
    meta: { showSearch: true, active: 'marketing' },
    component: () => import('@/views/Shared/Marketing.vue'),
    beforeEnter: (to, from, next) => {
      const { hasBaseAccess } = useAuthorisation()
      if (hasBaseAccess.value) {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    path: '/news',
    name: 'News',
    meta: { showSearch: true, active: 'news' },
    component: () => import('@/views/Shared/NewsPosts.vue'),
    beforeEnter: (to, from, next) => {
      const { hasBaseAccess } = useAuthorisation()
      if (hasBaseAccess.value) {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    path: '/news/category/:categoryId',
    name: 'News Category',
    meta: { active: 'news' },
    component: () => import('@/views/Shared/NewsPosts.vue'),
    beforeEnter: (to, from, next) => {
      const { hasBaseAccess } = useAuthorisation()
      if (hasBaseAccess.value) {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    path: '/news/:id',
    name: 'News Post',
    meta: { active: 'news' },
    component: () => import('@/views/Shared/News/NewsPost.vue'),
    beforeEnter: (to, from, next) => {
      const { hasBaseAccess } = useAuthorisation()
      if (hasBaseAccess.value) {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    path: '/visualiser',
    name: 'Visualiser Setup',
    component: () => import('@/views/Shared/VisualiserUpload.vue'),
    beforeEnter: (to, from, next) => {
      const basketStore = useBasketStore()
      if (basketStore.hasContract) {
        next()
      } else {
        next('/new-quote')
      }
    }
  },
  {
    path: '/visualiser/place',
    name: 'Visualiser',
    component: () => import('@/views/Shared/Visualiser/VisualiserPlace.vue'),
    beforeEnter: (to, from, next) => {
      const basketStore = useBasketStore()
      if (basketStore.hasContract) {
        const visualiserStore = useVisualiserStore()
        if (visualiserStore.images.length > 0 && visualiserStore.selectedImageId) {
          next()
        } else {
          next('/visualiser')
        }
      } else {
        next('/new-quote')
      }
    }
  },
  {
    path: '/payment/:contractId/success',
    name: 'Payment Sucesss',
    component: () => import('@/views/Portal/Payment/Success.vue')
  },
  {
    path: '/payment/:contractId/cancelled',
    name: 'Payment Failed',
    component: () => import('@/views/Portal/Payment/Failed.vue')
  }
]

export default routes.map((route) => {
  const meta = { ...route.meta, app: 'customer' }
  return { ...route, meta }
})
